import axios from "axios";
import jwt from "jsonwebtoken";
// 🔥 REMOVED: CryptoJS import - encryption removed for emoji support

const getUserIdFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwt.decode(token);
    return decoded.userId;
  } catch (error) {
    console.error("Token decode error:", error);
    return null;
  }
};

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch (error) {
    console.warn("SessionStorage unavailable:", error);
    return null;
  }
};

const cacheBypassPrefixes = [
  "property/mint",
  "property/updateProperty",
  "property/saveDraft",
  "property/deleteDraft",
  "property/publishDraft",
  "property/getProperty",
  "property/host/properties",
  "property/dashboard/traveler",
  "user/addToWishlist",
  "user/checkWishlist",
  "user/getWishlist",
];

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const isRouteCacheable = (route = "") => {
  if (!route) return false;
  if (route.includes("/chat/getMessages")) return false;
  return !cacheBypassPrefixes.some((prefix) => route.startsWith(prefix));
};

const buildCacheKey = (method, route, payload) =>
  `${method}:${route}-${JSON.stringify(payload || {})}`;

const getCachedResponse = (storage, cacheKey) => {
  if (!storage) return null;
  const cachedResponse = storage.getItem(cacheKey);
  if (!cachedResponse) return null;
  try {
    const parsed = JSON.parse(cachedResponse);
    if (Date.now() - parsed.timestamp < CACHE_DURATION) {
      return parsed.data;
    }
    storage.removeItem(cacheKey);
  } catch {
    storage.removeItem(cacheKey);
  }
  return null;
};

const setCachedResponse = (storage, cacheKey, data) => {
  if (!storage) return;
  try {
    storage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.warn("Failed to cache API response:", error);
  }
};

export const setAuthToken = () => {
  const userId = getUserIdFromToken();
  const tokenData = {
    userId,
    timestamp: Date.now(),
  };

  return (
    "Bearer " +
    jwt.sign(tokenData, process.env.REACT_APP_ACCESS_TOKEN_SECRET, {
      expiresIn: "1h",
    })
  );
};

// 🔥 REMOVED: encryptData function - no longer needed

export const api = async (route, data) => {
  try {
    const shouldCache = isRouteCacheable(route);
    const cacheKey = buildCacheKey("POST", route, data);
    const storage = shouldCache ? getSessionStorage() : null;

    if (shouldCache) {
      const cached = getCachedResponse(storage, cacheKey);
      if (cached) {
        return cached;
      }
    }

    const response = await axios.post(
      process.env.REACT_APP_BACKEND_SERVER + route,
      data || {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
      }
    );

    if (shouldCache) {
      setCachedResponse(storage, cacheKey, response.data);
    }

    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const apiGet = async (route, params) => {
  try {
    const shouldCache = isRouteCacheable(route);
    const cacheKey = buildCacheKey("GET", route, params);
    const storage = shouldCache ? getSessionStorage() : null;

    if (shouldCache) {
      const cached = getCachedResponse(storage, cacheKey);
      if (cached) {
        return cached;
      }
    }

    const response = await axios.get(
      process.env.REACT_APP_BACKEND_SERVER + route,
      {
        params: params || {},
        headers: {
          Authorization: setAuthToken(),
        },
      }
    );

    if (shouldCache) {
      setCachedResponse(storage, cacheKey, response.data);
    }

    return response.data;
  } catch (error) {
    console.error("API GET Error:", error);
    throw error;
  }
};


// ==================== DRAFT PROPERTY APIS ====================

/**
 * Save or update a property draft
 * @param {string} wallet_address - User's wallet address
 * @param {string} property_id - Optional existing draft ID
 * @param {object} draft_data - Draft property data
 * @param {number} current_step - Current step in creation flow (0-4)
 * @returns {Promise<object>} Response with property_id and success status
 */
export const saveDraft = async (wallet_address, property_id, draft_data, current_step) => {
  return api('property/saveDraft', {
    wallet_address,
    property_id,
    draft_data,
    current_step
  });
};

/**
 * Get all drafts for a wallet address
 * @param {string} wallet_address - User's wallet address
 * @returns {Promise<object>} Response with array of drafts
 */
export const getDrafts = async (wallet_address) => {
  return api('property/getDrafts', { wallet_address });
};

/**
 * Get a specific draft by property ID
 * @param {string} property_id - Draft property ID
 * @returns {Promise<object>} Response with draft data
 */
export const getDraft = async (property_id) => {
  return api('property/getDraft', { property_id });
};

/**
 * Delete a draft property
 * @param {string} property_id - Draft property ID
 * @param {string} wallet_address - User's wallet address
 * @returns {Promise<object>} Response with success status
 */
export const deleteDraft = async (property_id, wallet_address) => {
  return api('property/deleteDraft', { property_id, wallet_address });
};

/**
 * Publish a draft to convert it to a live property
 * @param {string} property_id - Draft property ID
 * @param {string} wallet_address - User's wallet address
 * @param {object} final_data - Final property data
 * @returns {Promise<object>} Response with success status
 */
export const publishDraft = async (property_id, wallet_address, final_data) => {
  return api('property/publishDraft', { property_id, wallet_address, final_data });
};

/**
 * Get comprehensive draft data with progress tracking
 * @param {string} wallet_address - User's wallet address
 * @param {boolean} include_draft_data - Whether to include full draft data
 * @returns {Promise<object>} Response with drafts and summary
 */
export const getAllDraftData = async (wallet_address, include_draft_data = true) => {
  return api('property/getAllDraftData', { wallet_address, include_draft_data });
};

/**
 * Get statistical overview of user's drafts
 * @param {string} wallet_address - User's wallet address
 * @returns {Promise<object>} Response with draft statistics
 */
export const getDraftStats = async (wallet_address) => {
  return api('property/getDraftStats', { wallet_address });
};

export const mintProperty = async (payload) => {
  return api("property/mint", payload);
};